import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const T = {
  title: { en: 'Scheduler & Automation', hi: 'शेड्यूलर व ऑटोमेशन' },
  subtitle: { en: 'Drip campaigns, automated tasks & scheduled jobs', hi: 'ड्रिप कैंपेन, स्वचालित कार्य व शेड्यूल किए गए जॉब्स' },
  task_scheduler: { en: 'Task Scheduler', hi: 'टास्क शेड्यूलर' },
  running: { en: 'Running — Automated tasks active', hi: 'चालू — स्वचालित कार्य सक्रिय हैं' },
  stopped: { en: 'Stopped — No tasks running', hi: 'बंद — कोई कार्य नहीं चल रहा' },
  start: { en: 'Start', hi: 'शुरू करें' },
  stop: { en: 'Stop', hi: 'रोकें' },
  run_drip: { en: 'Run Drip Campaign', hi: 'ड्रिप कैंपेन चलाएं' },
  run_drip_desc: { en: 'Send lead nurturing messages', hi: 'लीड नर्चरिंग संदेश भेजें' },
  attendance_alerts: { en: 'Attendance Alerts', hi: 'उपस्थिति अलर्ट' },
  attendance_alerts_desc: { en: 'Send alerts for absent students', hi: 'अनुपस्थित विद्यार्थियों की अलर्ट भेजें' },
  fee_reminders: { en: 'Fee Reminders', hi: 'शुल्क अनुस्मारक' },
  fee_reminders_desc: { en: 'Send pending fee reminders', hi: 'बकाया शुल्क अनुस्मारक भेजें' },
  recent_logs: { en: 'Recent Logs', hi: 'हाल के लॉग' },
  no_logs: { en: 'No logs found', hi: 'कोई लॉग नहीं है' },
};

export default function SchedulerPage({ token }) {
  const { language } = useLanguage();
  const tr = (k) => (T[k] ? T[k][language] || T[k].en : k);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');

  const headers = useCallback(() => ({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, logsRes] = await Promise.all([
        fetch(`${API_URL}/api/scheduler/status`, { headers: headers() }),
        fetch(`${API_URL}/api/scheduler/logs`, { headers: headers() }),
      ]);
      const statusData = await statusRes.json();
      const logsData = await logsRes.json();
      if (statusData.success !== false) setSchedulerStatus(statusData);
      if (logsData.success !== false) setLogs(logsData.logs || logsData || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [headers]);

  useEffect(() => { loadData(); }, [loadData]);

  const runAction = async (action) => {
    setActionLoading(action);
    try {
      const res = await fetch(`${API_URL}/api/scheduler/${action}`, { method: 'POST', headers: headers() });
      await res.json();
      loadData();
    } catch (err) { console.error(err); }
    setActionLoading('');
  };

  const quickActions = [
    { id: 'run-drip', label: tr('run_drip'), desc: tr('run_drip_desc'), icon: 'fa-paper-plane', color: 'bg-blue-500' },
    { id: 'run-attendance-alerts', label: tr('attendance_alerts'), desc: tr('attendance_alerts_desc'), icon: 'fa-bell', color: 'bg-amber-500' },
    { id: 'run-fee-reminders', label: tr('fee_reminders'), desc: tr('fee_reminders_desc'), icon: 'fa-indian-rupee-sign', color: 'bg-green-500' },
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="scheduler-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <i className="fa-solid fa-clock text-cyan-500"></i> {tr('title')}
          </h1>
          <p className="text-sm text-gray-500">{tr('subtitle')}</p>
        </div>
        <button onClick={loadData} className="p-2 hover:bg-gray-100 rounded-lg transition" data-testid="scheduler-refresh-btn">
          <i className="fa-solid fa-arrows-rotate text-gray-500"></i>
        </button>
      </div>

      {/* Status Card */}
      {schedulerStatus && (
        <div className={`rounded-xl border-2 p-5 mb-6 ${schedulerStatus.is_running ? 'border-emerald-200 bg-emerald-50/50' : 'border-gray-200 bg-gray-50'}`} data-testid="scheduler-status">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${schedulerStatus.is_running ? 'bg-emerald-500' : 'bg-gray-300'}`}>
                <i className={`fa-solid ${schedulerStatus.is_running ? 'fa-play' : 'fa-pause'} text-white text-xl`}></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-800">{tr('task_scheduler')}</h3>
                <p className="text-sm text-gray-500">
                  {schedulerStatus.is_running ? tr('running') : tr('stopped')}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => runAction('start')} disabled={actionLoading === 'start'}
                className="px-4 py-2 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600 disabled:opacity-50 transition flex items-center gap-2"
                data-testid="scheduler-start-btn">
                {actionLoading === 'start' ? <i className="fa-solid fa-spinner animate-spin"></i> : <i className="fa-solid fa-play"></i>} {tr('start')}
              </button>
              <button onClick={() => runAction('stop')} disabled={actionLoading === 'stop'}
                className="px-4 py-2 bg-gray-500 text-white rounded-xl text-sm font-medium hover:bg-gray-600 disabled:opacity-50 transition flex items-center gap-2"
                data-testid="scheduler-stop-btn">
                {actionLoading === 'stop' ? <i className="fa-solid fa-spinner animate-spin"></i> : <i className="fa-solid fa-pause"></i>} {tr('stop')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {quickActions.map(action => (
          <button key={action.id} onClick={() => runAction(action.id)} disabled={!!actionLoading}
            className="bg-white rounded-xl border p-4 text-left hover:shadow-md hover:border-blue-200 transition group disabled:opacity-50"
            data-testid={`action-${action.id}`}>
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${action.color} flex items-center justify-center group-hover:scale-110 transition`}>
                <i className={`fa-solid ${action.icon} text-white`}></i>
              </div>
              <div>
                <p className="font-semibold text-gray-700 text-sm">{action.label}</p>
                <p className="text-xs text-gray-400">{action.desc}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Logs */}
      <div className="bg-white rounded-xl border p-5" data-testid="scheduler-logs">
        <h3 className="font-bold text-gray-700 mb-4 flex items-center gap-2">
          <i className="fa-solid fa-list text-cyan-500"></i> {tr('recent_logs')}
        </h3>
        {loading ? (
          <div className="flex items-center justify-center h-24"><div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div></div>
        ) : logs.length === 0 ? (
          <p className="text-center text-gray-400 py-8">{tr('no_logs')}</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(Array.isArray(logs) ? logs : []).slice(0, 50).map((log, i) => (
              <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-gray-50 text-sm">
                <i className={`fa-solid fa-circle text-[6px] ${log.status === 'success' ? 'text-emerald-500' : log.status === 'error' ? 'text-red-500' : 'text-gray-400'}`}></i>
                <span className="text-gray-600 flex-1">{log.message || log.task || JSON.stringify(log)}</span>
                <span className="text-gray-400 text-xs shrink-0">{log.timestamp ? new Date(log.timestamp).toLocaleString(language === 'hi' ? 'hi-IN' : 'en-IN') : ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
